/**
 * TaskOverview — the counts at the top of the Event Hub landing page.
 *
 * Two variants, both headed "My Overview", because the two audiences have different
 * outstanding work:
 *
 *   marketer  Events to Publish · Nominations Awaiting My Review
 *   BD        Events Open for Nomination · My Nominations in Review
 *             · My Nominations to Confirm Attendance · Nominations Awaiting My Review (managers)
 *
 * Which panels render is decided by the page and passed in, not derived here from
 * roles. Two sections used to each work it out for themselves and drifted: an admin
 * got the marketer panel from one and the BD "New Events" list from the other, a
 * combination no real user has. One answer, computed once, is passed to both.
 *
 * Two counts come from the event list:
 *
 *   Events to Publish            events still in DRAFT
 *   Events Open for Nomination   nomination-enabled events whose window is open right now
 *
 * and the rest from the nomination list (see nominationCounts): `My Nominations in Review` —
 * what this BD submitted and is waiting on — and `Nominations Awaiting My Review` — what this
 * reader may decide (mayDecideNomination): any pending one for a marketer, a report's pending one
 * for a BD. A BD gets the latter only when their list shows them a report's nomination (they are
 * a manager).
 * Every count links to the list filtered to exactly what it counted.
 *
 * The design also draws Registration Follow-ups and MQL Follow-ups on the BD panel. They are
 * left out until there is data and an API for them (testing feedback): they are funnel actuals,
 * which today arrive only as an embedded QuickSight dashboard this app cannot count, and a tile
 * that can only ever show a dash is noise on a BD's first screen.
 */

import { useNavigate } from 'react-router-dom';
import type { Event, Nomination } from '../../client/EventCalendarClient';
import { isNominatable } from '../../utils/nominatableEvents';
import {
  AWAITING_MY_REVIEW_HREF,
  countAwaitingMyReview,
  countOwnAwaitingReview,
  countToConfirmAttendance,
  seesReportsNominations,
  toConfirmAttendanceHref,
} from '../../utils/nominationCounts';
import type { EventHubRole } from '../../utils/authorization';
import './TaskOverview.css';

const NO_ROLES: ReadonlySet<EventHubRole> = new Set();

/** One count. */
interface Tile {
  label: string;
  value: number;
  href?: string;
}

/** DRAFT events are the ones still awaiting a PublishEvent call. */
function countEventsToPublish(events: readonly Event[]): number {
  return events.filter((e) => e.contentStatus === 'DRAFT').length;
}

function marketerTiles(
  events: readonly Event[],
  nominations: readonly Nomination[],
  roles: ReadonlySet<EventHubRole>,
  alias: string | undefined,
): Tile[] {
  return [
    {
      label: 'Events to Publish',
      value: countEventsToPublish(events),
      /* The calendar's TABLE view on this same page, pre-filtered to the very events
         this count counted — there is no separate list page, the table is the list.
         `view=table` is not decoration: a draft usually has no startDate yet, so it has
         no cell on the month grid, and a link that landed there would show nothing. */
      href: '/event?view=table&status=DRAFT',
    },
    {
      /* What this marketer may decide: every pending nomination — the rows the Nomination List
         offers them Approve / Reject on. The link opens exactly those. */
      label: 'Nominations Awaiting My Review',
      value: countAwaitingMyReview(nominations, alias, roles),
      href: AWAITING_MY_REVIEW_HREF,
    },
  ];
}

function bdTiles(
  events: readonly Event[],
  nominations: readonly Nomination[],
  roles: ReadonlySet<EventHubRole>,
  alias: string | undefined,
  now: Date,
  bdBusinessUnits: readonly string[] | undefined,
): Tile[] {
  const tiles: Tile[] = [
    {
      label: 'Events Open for Nomination',
      // Same predicate the Upcoming Events cards use for their open group.
      value: events.filter((e) => isNominatable(e, now, bdBusinessUnits)).length,
      /* The calendar's table view on this page, filtered to exactly these events by the
         Nomination filter — which uses the same predicate — so what opens is what was counted.
         Table, not month: a list is what "which ones" asks for. */
      href: '/event?view=table&nomination=OPEN',
    },
    {
      /* A BD's OWN submissions still awaiting a decision. A BD manager also sees their
         reports' nominations, so both the count and the link filter by alias. */
      label: 'My Nominations in Review',
      value: countOwnAwaitingReview(nominations, alias),
      href: alias
        ? `/event/nominations?status=PENDING_REVIEW&createdBy=${encodeURIComponent(alias)}`
        : '/event/nominations?status=PENDING_REVIEW',
    },
  ];
  /* The BD's own approved nominations whose guest's attendance they still have to confirm. The
     link lands on exactly those, so each box can be ticked without touching the filters. */
  tiles.push({
    label: 'My Nominations to Confirm Attendance',
    value: countToConfirmAttendance(nominations, alias),
    href: toConfirmAttendanceHref(alias),
  });
  /* Managers only: a BD whose list holds someone else's nomination is seeing their reports'
     (seesReportsNominations), and those are theirs to review. */
  if (seesReportsNominations(nominations, alias)) {
    tiles.push({
      label: 'Nominations Awaiting My Review',
      value: countAwaitingMyReview(nominations, alias, roles),
      href: AWAITING_MY_REVIEW_HREF,
    });
  }
  return tiles;
}

export interface TaskOverviewProps {
  /**
   * Whether to render the marketer panel. A view decision, NOT a capability check —
   * the page decides which audience's landing surface this is, and passes the same
   * answer to every section so they cannot disagree. See EventHubHomePage.
   */
  viewAsMarketer: boolean;
  /** Whether to render the BD panel. Both can be true. */
  viewAsBd: boolean;
  /** Every event, as fetched by the page. Counts are derived from this. */
  events: readonly Event[];
  /** Every nomination the caller may see, as fetched by the page. */
  nominations?: readonly Nomination[];
  /**
   * The caller's login alias, matched against a nomination's `createdBy` to tell a BD's own
   * submissions from their reports'. Absent means no nomination counts as theirs.
   */
  alias?: string;
  /** True while either list is in flight; the tiles show a dash meanwhile. */
  loading?: boolean;
  /** The roles Event Hub renders for — who may decide which nomination (mayDecideNomination). */
  roles?: ReadonlySet<EventHubRole>;
  /**
   * The caller's BUs when they nominate: "Events Open for Nomination" counts only events for
   * them (utils/nominatableEvents). Undefined counts every open event.
   */
  bdBusinessUnits?: readonly string[];
}

export default function TaskOverview({
  viewAsMarketer,
  viewAsBd,
  events,
  nominations = [],
  alias,
  loading,
  roles = NO_ROLES,
  bdBusinessUnits,
}: TaskOverviewProps) {
  const navigate = useNavigate();
  // `now` is read once per render rather than per tile so every count in one paint
  // agrees on what "today" is.
  const now = new Date();

  const panels: { title: string; tiles: Tile[] }[] = [];
  if (viewAsMarketer) {
    panels.push({
      title: 'My Overview',
      tiles: marketerTiles(events, nominations, roles, alias),
    });
  }
  if (viewAsBd) {
    panels.push({
      title: 'My Overview',
      tiles: bdTiles(events, nominations, roles, alias, now, bdBusinessUnits),
    });
  }

  // A viewer-only caller has no outstanding work by definition; rendering an empty
  // card would just be furniture.
  if (panels.length === 0) return null;

  return (
    <>
      {panels.map((panel) => (
        /* A plain card, not an AWSUI Container: the Figma's card is a 1px border with no
           shadow and its own spacing (see TaskOverview.css), and Container's shadow, header
           padding and header-to-content gap are all fixed by its styles. */
        <section key={panel.title} className="eh-task-card" aria-label={panel.title}>
          <h2 className="eh-task-card__title">{panel.title}</h2>
          <div className="eh-task-overview">
            {panel.tiles.map((tile) => {
              const shown = loading ? '—' : tile.value;
              return (
                <div key={tile.label} className="eh-task-overview__tile">
                  {/* The COUNT is the link, not the label — that is how the Figma draws
                      it, and it is the number that carries the "there are N of these
                      waiting" meaning worth acting on. A dash (still loading) is not a
                      link: there is nothing to go and look at until the count is real. */}
                  <div
                    className="eh-task-overview__count"
                    title={loading ? 'Loading…' : undefined}
                  >
                    {tile.href && !loading ? (
                      /* A plain <a>, NOT an AWSUI Link. The Figma draws this number in
                         its own colour with no underline, and AWSUI's Link sets both
                         through a two-class selector that out-specifies anything written
                         here — overriding it took a growing pile of !important-adjacent
                         resets. A bare anchor inherits the tile's type instead, so the
                         design is expressed once, where the rest of the type lives.

                         Still a real href: middle-click and "open in new tab" work, and
                         the anchor is focusable and Enter-activatable for free. */
                      <a
                        href={tile.href}
                        className="eh-task-overview__count-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(tile.href!);
                        }}
                      >
                        {shown}
                      </a>
                    ) : (
                      shown
                    )}
                  </div>
                  <div className="eh-task-overview__label">{tile.label}</div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}
